import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import { TabPanel } from "@mui/lab";
import { Button, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import useTabSearchParams from "@entrilia/shared/hooks/useTabSearchParams";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import useClientContext from "../../../../context/useClientContext";
import { pageRoutes } from "../../../../routes";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import PageTabs from "../../../common/PageTabs";
import TabLabelWithCount from "../../../common/TabLabelWithCount";
import UploadDocumentsButton from "./UploadDocumentsButton";
import WorkflowRunsList from "./WorkflowRunsList";

const tabValues = ["active", "need_action", "completed", "failed"] as const;

const tabPanelSx = { flex: 1, px: 3, pt: 2.5, width: "100%" };

const WorkflowRunsPage = () => {
  usePageTitle("Workflow Runs");

  const { clientCode } = useClientContext();

  const { templateId } = useParams();

  const navigate = useNavigate();

  const { selectedTab, onTabChange } = useTabSearchParams(tabValues);

  const getWorkflowGroup = useCallback(() => adminApi.getWorkflowGroup(templateId ?? ""), [templateId]);

  const [workflowGroup, fetchError] = useFetch(getWorkflowGroup);

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleWorkflowsStarted = () => {
    setRefreshTrigger((n) => n + 1);
  };

  if (!templateId) {
    return <Navigate to=".." />;
  }

  if (fetchError) {
    logError(fetchError, "[WorkflowRunsPage] getWorkflowGroup");
    return <DataLoadingFailed title="Loading workflow template details failed" />;
  }

  if (workflowGroup === undefined) {
    return <InlineLoader />;
  }

  const { name, latestTemplateVersionId, workflowFieldsSchema } = workflowGroup;

  return (
    <>
      <GeneralPageHeader
        TitleComponent={
          <Stack spacing={0.5}>
            <Typography variant="h6">{name}</Typography>
            <Typography variant="caption" color="textSecondary">
              Workflow Runs
            </Typography>
          </Stack>
        }
        hideBorderLine
        showDefaultBackButtonTo=".."
      >
        <UploadDocumentsButton
          workflowType={workflowGroup.workflowType}
          templateId={templateId}
          onWorkflowsStarted={handleWorkflowsStarted}
        />
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<SettingsIcon />}
          onClick={() =>
            navigate(
              `/${clientCode}/${pageRoutes.workflows}/${pageRoutes.workflowTemplates}/${latestTemplateVersionId}`
            )
          }
        >
          Template
        </Button>
      </GeneralPageHeader>
      <PageTabs
        tabs={tabValues}
        labels={[
          "Active",
          <TabLabelWithCount
            key="need_action"
            label="Need Action"
            count={workflowGroup.workflowsRequiringUserActionCount}
            badgeColor="warning"
          />,
          "Completed",
          "Failed",
        ]}
        value={selectedTab}
        onChange={onTabChange}
      >
        <TabPanel value="active" sx={tabPanelSx}>
          <WorkflowRunsList
            target="active"
            templateId={templateId}
            workflowFieldsSchema={workflowFieldsSchema}
            refreshTrigger={refreshTrigger}
          />
        </TabPanel>
        <TabPanel value="need_action" sx={tabPanelSx}>
          <WorkflowRunsList
            target="need_action"
            templateId={templateId}
            workflowFieldsSchema={workflowFieldsSchema}
            refreshTrigger={refreshTrigger}
          />
        </TabPanel>
        <TabPanel value="completed" sx={tabPanelSx}>
          <WorkflowRunsList
            target="completed"
            templateId={templateId}
            workflowFieldsSchema={workflowFieldsSchema}
            refreshTrigger={refreshTrigger}
          />
        </TabPanel>
        <TabPanel value="failed" sx={tabPanelSx}>
          <WorkflowRunsList
            target="failed"
            templateId={templateId}
            workflowFieldsSchema={workflowFieldsSchema}
            refreshTrigger={refreshTrigger}
          />
        </TabPanel>
      </PageTabs>
    </>
  );
};

export default WorkflowRunsPage;
